<script setup>
import { computed, ref, watch } from 'vue'
import { set } from '@vueuse/core'
import { useUserStore } from '~/store/user'

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  tabs: {
    type: Array,
    default: null,
  },
  tab: {
    type: String,
    default: '',
  },
  tabFullWidth: {
    type: Boolean,
    default: false,
  },
  headerClass: {
    type: String,
    default: '',
  },
  componentClass: {
    type: String,
    default: '',
  },
  needLogin: {
    type: Boolean,
    default: false,
  },
  isLogin: {
    type: Boolean,
  },
  isNeedScroll: {
    type: Boolean,
    default: true,
  },
  componentProps: {
    type: Object,
    default: () => ({}),
  },
})
const emit = defineEmits(['tabChange'])
const tab = ref(props.tab)
const user = useUserStore()
const curTabData = computed(() => props.tabs?.find(i => i.key === tab.value) || props.tabs?.[0] || {})
watch(curTabData, ({ cb, key }) => cb && cb(key), { immediate: true })
function tabChange({ key, disabled }) {
  if (disabled)
    return
  emit('tabChange', key)
  set(tab, key)
}
watch(() => props.tab, () => {
  tab.value = props.tab
})
</script>

<template>
  <div class="flex flex-col">
    <header class="flex flex-shrink-0 h-12 border-b border-dividerPrimary text-textTertiary" :class="[{ 'px-4 gap-x-6': !tabFullWidth }, headerClass]">
      <template v-if="tabs">
        <div
          v-for="i in tabs"
          :key="i.key"
          class="inline-flex items-center h-full font-medium transition-colors border-transparent cursor-pointer select-none border-b-[2px] hover:text-v"
          :class="{ '!border-textPrimary border-b': curTabData.key === i.key && tabs.length > 1, 'text-textPrimary': curTabData.key === i.key, 'flex-1': tabFullWidth, '!cursor-not-allowed': i.disabled }"
          @click="tabChange(i)"
        >
          {{ i.title }}
          <slot :name="`tab-${i.key}`" v-bind="i">
            <span v-if="i.suffix">({{ i.suffix }})</span>
          </slot>
        </div>
      </template>
      <template v-else>
        <div class="h-8 px-3 -mb-px cursor-pointer bg-bg2">
          {{ title }}
        </div>
      </template>
      <div class="!ms-auto flex items-center gap-x-6 text-xs">
        <slot :name="`actions-${curTabData.key}`" v-bind="curTabData" />
        <slot name="actions" v-bind="curTabData" />
      </div>
    </header>
    <n-scrollbar x-scrollable class="px-4" :class="componentClass">
      <slot name="prefix" />
      <slot v-bind="curTabData">
        <template v-if="(needLogin && user.isLogin)">
          <ClientOnly>
            <component :is="curTabData.component" v-bind="curTabData.props || componentProps" />
          </ClientOnly>
        </template>
        <component :is="curTabData.component" v-else-if="!needLogin" v-bind="curTabData.props || componentProps" />
      </slot>
      <slot name="append" />
    </n-scrollbar>
  </div>
</template>
